'use client';

import { InputAdornment, Tooltip } from '@mui/material';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { useQuery } from '@parkchirp/api-client';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FeeTypeEnumDisplay } from '../../enums';
import { GET_SUBLOTS } from '../../queries';
import type { DownloadReport, SetShowSnackbar } from '../../types';
import { formatDateParkerStatus } from '../../utils';
import { ReportActionButton } from './reportActionButton';

interface InvoiceReportProps {
	facilityId: string;
	downloadReport: DownloadReport;
	setShowSnackbar: SetShowSnackbar;
	forTenant?: boolean;
	prefillAccountId?: string;
	prefillSubName?: string;
}

const INVOICE_REPORT = 'InvoiceReport';
const INVOICE_MONTHLY_REPORT = 'InvoiceMonthlyReport';
const VALIDATION_AND_OTHER_DETAIL_REPORT = 'InvoiceValidationReport';

export const InvoiceReportForm: FC<InvoiceReportProps> = ({
	facilityId,
	downloadReport,
	setShowSnackbar,
	forTenant = false,
	prefillAccountId,
	prefillSubName,
}) => {
	const [reportType, setReportType] = useState<string>(INVOICE_REPORT);
	const [feeType, setFeeType] = useState<string[]>([]);
	const [feeTypesToUse, setFeeTypesToUse] = useState<string[]>([]);
	const [invoiceStartDate, setInvoiceStartDate] = useState<string | null>(null);
	const [invoiceEndDate, setInvoiceEndDate] = useState<string | null>(null);
	const [parkingStartMonth, setParkingStartMonth] = useState<string>();
	const [parkingStartYear, setParkingStartYear] = useState<string>();
	const [parkingEndMonth, setParkingEndMonth] = useState<string>();
	const [parkingEndYear, setParkingEndYear] = useState<string>();
	const [accountId, setAccountId] = useState<string>();
	const [subName, setSubName] = useState<string>();
	const [subscriberType, setSubscriberType] = useState<string>('All');
	const [openSubscriberTypeTooltip, setOpenSubscriberTypeTooltip] = useState<boolean>(false);
	const [checkedTypes, setCheckedTypes] = useState<Record<string, boolean>>({});
	const [selectAll, setSelectAll] = useState<boolean>(true);
	const [isDownloadingPdf, setIsDownloadingPdf] = useState<boolean>(false);
	const [isDownloadingCsv, setIsDownloadingCsv] = useState<boolean>(false);
	const [selectedSublotId, setSelectedSublotId] = useState<null | number>(null);
	const [selectedSublotName, setSelectedSublotName] = useState<null | string>(null);

	const { data: sublotsData } = useQuery(GET_SUBLOTS, {
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
		variables: {
			facilityId,
			includePrimary: true,
		},
		skip: !facilityId,
		fetchPolicy: 'network-only',
	});

	const reportTypes = useMemo(
		() => [
			{ label: 'Invoice Report', value: INVOICE_REPORT },
			{ label: 'Invoice Monthly Detail Report', value: INVOICE_MONTHLY_REPORT },
			{ label: 'Validation & Other Detail Report', value: VALIDATION_AND_OTHER_DETAIL_REPORT },
			{ label: 'Adjustment Report', value: 'InvoiceAdjustmentReport' },
		],
		[],
	);

	const monthOptions = useMemo(
		() => [
			{ label: 'January', value: '0' },
			{ label: 'February', value: '1' },
			{ label: 'March', value: '2' },
			{ label: 'April', value: '3' },
			{ label: 'May', value: '4' },
			{ label: 'June', value: '5' },
			{ label: 'July', value: '6' },
			{ label: 'August', value: '7' },
			{ label: 'September', value: '8' },
			{ label: 'October', value: '9' },
			{ label: 'November', value: '10' },
			{ label: 'December', value: '11' },
		],
		[],
	);

	const disableSubscriberType = useMemo(
		() =>
			forTenant ||
			![INVOICE_REPORT, INVOICE_MONTHLY_REPORT, VALIDATION_AND_OTHER_DETAIL_REPORT].includes(reportType),
		[reportType, forTenant],
	);

	const feeOrRateType = useMemo(
		() => (reportType === 'InvoiceMonthlyReport' ? 'Fee Type' : 'Rate Type'),
		[reportType],
	);

	const getYearOptions = useCallback(() => {
		const startYear = 2021;
		const endYear = new Date().getFullYear() + 1;
		const yearOptions = [];
		for (let i = startYear; i <= endYear; i++) {
			yearOptions.push({ label: i.toString(), value: i });
		}
		return yearOptions;
	}, []);

	useEffect(() => {
		if (selectAll && feeTypesToUse) {
			const initialCheckedItems: Record<string, boolean> = {};
			feeTypesToUse.forEach((fee) => {
				initialCheckedItems[fee] = true;
			});
			setCheckedTypes(initialCheckedItems);
		}
	}, [selectAll, feeTypesToUse]);

	const handleSingleCheck = useCallback(
		(value: string) => {
			setCheckedTypes((prev) => {
				const updatedItems = {
					...prev,
					[value]: !prev[value],
				};

				const allChecked = feeTypesToUse?.every((fee) => updatedItems[fee]) ?? false;
				setSelectAll(allChecked);

				return updatedItems;
			});
		},
		[feeTypesToUse],
	);

	const handleSelectAllCheck = useCallback(() => {
		if (selectAll) {
			setCheckedTypes({});
		} else {
			const newCheckedItems: Record<string, boolean> = {};
			feeTypesToUse?.forEach((fee) => {
				newCheckedItems[fee] = true;
			});
			setCheckedTypes(newCheckedItems);
		}
		setSelectAll(!selectAll);
	}, [feeTypesToUse, selectAll]);

	useEffect(() => {
		const checkedFeeTypes = Object.keys(checkedTypes).filter((fee) => checkedTypes[fee]);
		setFeeType(checkedFeeTypes);
	}, [checkedTypes]);

	useEffect(() => {
		setFeeType([]);
		const initialCheckedItems: Record<string, boolean> = {};
		setCheckedTypes(initialCheckedItems);
		if (reportType === 'InvoiceReport') {
			setFeeTypesToUse(['Monthly', 'Validation & Other Types']);
		} else if (reportType === 'InvoiceMonthlyReport') {
			setFeeTypesToUse([
				'Monthly',
				'Activation',
				'Access Device',
				'Credit Card',
				'Late',
				'Transaction',
				'Mail Invoice',
				'Vehicle Surcharge',
			]);
		} else if (reportType === 'InvoiceValidationReport') {
			setFeeTypesToUse(['Validation', 'Hotel Storage', 'Office/Retail Rent', 'Miscellaneous']);
		} else if (reportType === 'InvoiceAdjustmentReport') {
			setFeeTypesToUse(['Monthly', 'Validation', 'Hotel Storage', 'Office/Retail Rent', 'Miscellaneous']);
		}
	}, [reportType]);

	const formatFeeType = useCallback((str: string) => {
		// see PCA-2090 for logic behind returning these arrays
		if (str === FeeTypeEnumDisplay.MONTHLY) {
			return ['MONTHLY', 'MANUAL_MONTHLY'];
		}
		if (str === FeeTypeEnumDisplay.LATE) {
			return ['LATE', 'RECURRING_LATE'];
		}
		if (str === FeeTypeEnumDisplay.VALIDATION) {
			return ['VALIDATION', 'PREPAID_VALIDATION', 'BILL_BACK_VALIDATION'];
		}
		if (str === 'Validation & Other Types') {
			return ['VALIDATION_OTHER'];
		}
		return str.toUpperCase().replace(' ', '_').replace('/', '_').replace('&', '_');
	}, []);

	const downloadParams = useMemo(() => {
		const baseParams = {
			facilityId,
			accountId: accountId || '',
			subscriptionName: subName || '',
			...(prefillAccountId && { accountId: prefillAccountId }),
			...(prefillSubName && { subscriptionName: prefillSubName }),
			...(!disableSubscriberType && { subscriberType }),
			...(selectedSublotId && { sublotId: selectedSublotId }),
			...(selectedSublotName && { sublotName: selectedSublotName }),
		};

		const feeOrRateTypeFilters = feeType?.map(formatFeeType).flat();
		let feeOrRateTypeParams: any = {
			rateType: feeOrRateTypeFilters,
		};
		if (reportType === 'InvoiceMonthlyReport') {
			feeOrRateTypeParams = {
				feeType: feeOrRateTypeFilters,
			};
		}

		const dateParams: {
			invoiceStartDate?: string;
			invoiceEndDate?: string;
			parkingPeriodStartMonth?: string;
			parkingPeriodStartYear?: string;
			parkingPeriodEndMonth?: string;
			parkingPeriodEndYear?: string;
		} = {};

		if (invoiceStartDate) {
			dateParams.invoiceStartDate = formatDateParkerStatus(invoiceStartDate);
		}
		if (invoiceEndDate) {
			dateParams.invoiceEndDate = formatDateParkerStatus(invoiceEndDate);
		}
		if (parkingStartMonth && parkingStartYear) {
			dateParams.parkingPeriodStartMonth = parkingStartMonth;
			dateParams.parkingPeriodStartYear = parkingStartYear.toString();
		}
		if (parkingEndMonth && parkingEndYear) {
			dateParams.parkingPeriodEndMonth = parkingEndMonth;
			dateParams.parkingPeriodEndYear = parkingEndYear.toString();
		}

		return { ...baseParams, ...feeOrRateTypeParams, ...dateParams };
	}, [
		disableSubscriberType,
		facilityId,
		feeType,
		invoiceStartDate,
		invoiceEndDate,
		parkingStartMonth,
		parkingStartYear,
		parkingEndMonth,
		parkingEndYear,
		accountId,
		subName,
		formatFeeType,
		reportType,
		prefillAccountId,
		prefillSubName,
		selectedSublotId,
		selectedSublotName,
		subscriberType,
	]);

	const handleValidation = useCallback(() => {
		const validations = [
			{
				message: `Please select a report type.`,
				failingCondition: !reportType,
			},
			{
				message: `Please provide a fee or rate type.`,
				failingCondition: !feeType || feeType.length === 0,
			},
			{
				message: ` Please provide an invoice start date or a parking period.`,
				// the below basically means if the user didn't specify an invoice start date
				// or didn't specify a combination of parking period month and year
				failingCondition:
					!invoiceStartDate &&
					!((parkingStartMonth && parkingStartYear) || (parkingEndMonth && parkingEndYear)),
			},
			{
				message: ` Parking start time should be before parking end time.`,
				failingCondition:
					parkingStartYear &&
					parkingEndYear &&
					(parkingStartYear > parkingEndYear ||
						(parkingStartYear === parkingEndYear && Number(parkingStartMonth) > Number(parkingEndMonth))),
			},
		];
		const failingValidations = validations.filter(({ failingCondition }) => failingCondition);
		if (failingValidations.length) {
			setShowSnackbar({
				message: failingValidations[0].message,
				severity: 'error',
				onClose: () => {
					setShowSnackbar(null);
				},
			});
			return false;
		}
		return true;
	}, [
		reportType,
		feeType,
		invoiceStartDate,
		parkingStartMonth,
		parkingStartYear,
		parkingEndMonth,
		parkingEndYear,
		setShowSnackbar,
	]);

	const handleReset = useCallback(() => {
		setReportType(INVOICE_REPORT);
		setInvoiceStartDate(null);
		setInvoiceEndDate(null);
		setParkingStartMonth('');
		setParkingStartYear('');
		setParkingEndMonth('');
		setParkingEndYear('');
		setAccountId('');
		setSubName('');
		setSelectedSublotId(null);
		setSelectedSublotName(null);
		setSubscriberType('All');
		setSelectAll(true);
		setSelectedSublotId(null);
		setSelectedSublotName(null);
	}, []);

	return (
		<Box sx={{ padding: 3 }}>
			<Grid container spacing={2}>
				<Grid item xs={3}>
					<FormControl fullWidth variant='outlined' required>
						<InputLabel>Report Type</InputLabel>
						<Select
							label='Report Type'
							value={reportType || ''}
							onChange={(e) => setReportType(e.target.value)}
							data-cy='report-type-select-menu'
						>
							{reportTypes?.map((report) => (
								<MenuItem key={report.label} value={report.value}>
									{report.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
				<Grid item xs={2}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker
							label='Invoice Start Date'
							value={invoiceStartDate}
							slotProps={{
								textField: {
									fullWidth: true,
									inputProps: { 'data-cy': 'invoice-start-date-picker' },
								},
							}}
							onChange={setInvoiceStartDate}
						/>
					</LocalizationProvider>
				</Grid>
				<Grid item xs={4}>
					<Grid container spacing={1}>
						<Grid item xs={8}>
							<FormControl fullWidth variant='outlined'>
								<InputLabel>Parking Period Start Month</InputLabel>
								<Select
									label='Parking Period Start Month'
									value={parkingStartMonth || ''}
									onChange={(e) => setParkingStartMonth(e.target.value)}
									data-cy='parking-period-start-select-menu'
								>
									{monthOptions.map((month) => (
										<MenuItem key={month.value} value={month.value}>
											{month.label}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>
						<Grid item xs={4}>
							<FormControl fullWidth variant='outlined'>
								<InputLabel>Year</InputLabel>
								<Select
									label='Year'
									value={parkingStartYear || ''}
									onChange={(e) => setParkingStartYear(e.target.value)}
									data-cy='start-year-select-menu'
								>
									{getYearOptions().map((option) => (
										<MenuItem key={option.value} value={option.value}>
											{option.label}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>
					</Grid>
				</Grid>
				<Grid item xs={3}>
					<TextField
						type='number'
						fullWidth
						variant='outlined'
						label='Account ID'
						disabled={forTenant}
						value={prefillAccountId || accountId || ''}
						onChange={(e) => setAccountId(e.target.value)}
						onKeyDown={(event) => {
							if (event?.key === '-' || event?.key === '+' || event?.key === 'e') {
								event.preventDefault();
							}
						}}
						onPaste={(event) => {
							event.preventDefault();

							const paste = event.clipboardData.getData('text');
							const cleanedPaste = /^[AC]*[-]*(\d+)$/.exec(paste);

							if (cleanedPaste) {
								setAccountId(cleanedPaste[1]);
							}
						}}
						inputProps={{ 'data-cy': 'account-id-input' }}
						InputProps={{
							startAdornment: <InputAdornment position='start'>AC-</InputAdornment>,
						}}
					/>
				</Grid>
				<Grid item xs={3}>
					<FormControl fullWidth variant='outlined' required>
						<InputLabel id='fee-or-rate-type-label' shrink>
							{feeOrRateType}
						</InputLabel>
						<Select
							labelId='fee-or-rate-type-label'
							label={feeOrRateType}
							notched
							value={feeType}
							multiple
							displayEmpty
							renderValue={(selected) => {
								if (selectAll) return 'All Selected';
								if (selected.length === 0) return 'None Selected';
								if (selected.length === 1) return selected[0];
								return `${selected.length} types are selected`;
							}}
							data-cy='rate-type-select-menu'
						>
							<MenuItem value=''>
								<Checkbox checked={selectAll} onChange={handleSelectAllCheck} />
								<ListItemText primary={selectAll ? 'All Selected' : 'None Selected'} />
							</MenuItem>
							{feeTypesToUse?.map((fee) => (
								<MenuItem key={fee} value={fee}>
									<Checkbox
										checked={checkedTypes[fee] || false}
										onChange={() => handleSingleCheck(fee)}
									/>
									<ListItemText primary={fee} />
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
				<Grid item xs={2}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker
							label='Invoice End Date'
							value={invoiceEndDate}
							slotProps={{
								textField: {
									fullWidth: true,
									inputProps: { 'data-cy': 'invoice-end-date-picker' },
								},
							}}
							onChange={setInvoiceEndDate}
						/>
					</LocalizationProvider>
				</Grid>
				<Grid item xs={4}>
					<Grid container spacing={1}>
						<Grid item xs={8}>
							<FormControl fullWidth variant='outlined'>
								<InputLabel>Parking Period End Month</InputLabel>
								<Select
									label='Parking Period End Month'
									value={parkingEndMonth || ''}
									onChange={(e) => setParkingEndMonth(e.target.value)}
									data-cy='parking-period-end-select-menu'
								>
									{monthOptions.map((month, index) => (
										<MenuItem key={index} value={month.value}>
											{month.label}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>
						<Grid item xs={4}>
							<FormControl fullWidth variant='outlined'>
								<InputLabel>Year</InputLabel>
								<Select
									label='Year'
									value={parkingEndYear || ''}
									onChange={(e) => setParkingEndYear(e.target.value)}
									data-cy='end-year-select-menu'
								>
									{getYearOptions().map((option, index) => (
										<MenuItem key={index} value={option.value}>
											{option.label}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>
					</Grid>
				</Grid>
				<Grid item xs={3}>
					<FormControl fullWidth variant='outlined'>
						<TextField
							fullWidth
							variant='outlined'
							label='Sub Name'
							disabled={forTenant}
							value={prefillSubName || subName || ''}
							onChange={(e) => setSubName(e.target.value)}
							inputProps={{ 'data-cy': 'sub-name-input' }}
						/>
					</FormControl>
				</Grid>
				<Grid item xs={3}>
					<Tooltip
						title={
							!disableSubscriberType
								? 'Subscriber Type will only filter data and be visible on CSV. Not PDFs'
								: ''
						}
						open={openSubscriberTypeTooltip}
						onMouseEnter={() => setOpenSubscriberTypeTooltip(true)}
						onMouseLeave={() => setOpenSubscriberTypeTooltip(false)}
						placement='bottom'
					>
						<FormControl fullWidth variant='outlined'>
							<InputLabel id='subscriber-type-label'>Subscriber Type</InputLabel>
							<Select
								onOpen={() => setOpenSubscriberTypeTooltip(!openSubscriberTypeTooltip)}
								fullWidth
								label='Subscriber Type'
								labelId='subscriber-type-label'
								id='subscriber-type-id'
								disabled={disableSubscriberType}
								value={subscriberType}
								onChange={(e) => setSubscriberType(e.target.value)}
								data-cy='subscriber-type-select-menu'
							>
								<MenuItem value='All'>All</MenuItem>
								<MenuItem value='Business'>Business</MenuItem>
								<MenuItem value='Individual'>Individual</MenuItem>
							</Select>
						</FormControl>
					</Tooltip>
				</Grid>
				<Grid item xs={3}>
					<FormControl fullWidth variant='outlined'>
						<InputLabel>Sublot</InputLabel>
						<Select
							label='Sublot'
							value={selectedSublotId || ''}
							onChange={(e) => {
								const subId = Number(e.target.value);
								setSelectedSublotId(subId);
								const selectedSublot = sublotsData?.getSublots.find((d) => d.id === subId);
								setSelectedSublotName(selectedSublot.name);
							}}
						>
							{sublotsData?.getSublots?.map((sublot, index) => (
								<MenuItem key={index} value={sublot.id}>
									{sublot.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
			</Grid>
			<ReportActionButton
				disableDownloadButtons={!facilityId}
				downloadReport={(format) =>
					downloadReport(format, {
						reportType: reportType as string,
						params: downloadParams,
						handleValidation,
						setIsDownloadingCsv,
						setIsDownloadingPdf,
					})
				}
				isDownloadingPdf={isDownloadingPdf}
				isDownloadingCsv={isDownloadingCsv}
				onReset={handleReset}
			/>
		</Box>
	);
};
